<template>
  <multiselect
    :id="multiselectId"
    ref="multiselect"
    :model-value="modelValue"
    :internal-search="!isFuzzyMatching"
    :options="filteredOptions"
    :open-direction="openDirection"
    :placeholder="placeholder"
    :label="label"
    :track-by="trackBy"
    :preselect-first="preselectFirst"
    :searchable="searchable"
    :preserve-search="preserveSearch"
    :taggable="taggable"
    :max-height="maxHeight"
    :disabled="disabled"
    :multiple="multiple"
    :allow-empty="allowEmpty"
    :hide-selected="hideSelected"
    :close-on-select="closeOnSelect"
    :show-no-results="showNoResults"
    :show-no-options="showNoOptions"
    :group-values="groupValues"
    :group-label="groupLabel"
    :group-select="groupSelect"
    :prevent-autofocus="preventAutofocus"
    :block-keys="blockKeys"
    class="qx-multiselect"
    :class="{ 'error-select': isError, 'narrowed-tags-wrap': showClearAll }"
    @select="(value) => $emit('select', value)"
    @remove="(value) => $emit('remove', value)"
    @update:model-value="onInput"
    @search-change="onSearchChange"
    @tag="(value) => $emit('tag', value)"
    @open="onOpen"
    @close="onClose">
    <template
      v-if="showClearAll && modelValue.length > 0"
      #clear>
      <em
        class="qx-icon icon-cancel-circle"
        :title="string.clearAllTitle"
        @mousedown.prevent.stop="$emit('clear-all')" />
    </template>
    <template #caret="{ toggle }">
      <em
        class="qx-icon icon-arrow-down-sm list-open-icon"
        @mousedown.prevent.stop="toggle()" />
    </template>
    <template #singleLabel="{ option }">
      <slot
        name="singleLabel"
        :option="option" />
    </template>
    <template #option="{ option }">
      <slot
        name="option"
        :option="option" />
    </template>
    <template #beforeList>
      <slot name="beforeList" />
    </template>
    <template #noResult>
      <span class="qx-multiselect-no-result">{{ noResult }}</span>
    </template>
    <template #noOptions>
      <span class="qx-multiselect-no-options">{{ noOptions }}</span>
    </template>
  </multiselect>
</template>

<script>
import { nextTick } from 'vue';
import Multiselect from 'vue-multiselect';
import sortComparator from '@/utils/sortComparator.js';
import { searchFuzzyMatches } from '@/utils/filters.js';

export default {
  name: 'QxMultiselect',
  components: {
    Multiselect,
  },
  inject: {
    modal: {
      default: null,
    },
  },
  props: {
    modelValue: {
      type: [String, Array, Object],
      default: '',
    },
    idPrefix: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    openDirection: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'text',
    },
    trackBy: {
      type: String,
      default: 'value',
    },
    preselectFirst: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    preserveSearch: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noResult: {
      type: String,
      default: '',
    },
    noOptions: {
      type: String,
      default: '',
    },
    allowEmpty: {
      type: Boolean,
      default: true,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    showNoResults: {
      type: Boolean,
      default: true,
    },
    showNoOptions: {
      type: Boolean,
      default: true,
    },
    groupValues: {
      type: String,
      default: '',
    },
    groupLabel: {
      type: String,
      default: '',
    },
    groupSelect: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: Number,
      default: 230,
    },
    preventAutofocus: {
      type: Boolean,
      default: false,
    },
    blockKeys: {
      type: Array,
      default: () => [],
    },
    clearSearchTrigger: {
      type: Boolean,
      default: false,
    },
    closeTrigger: {
      type: Boolean,
      default: false,
    },
    isFuzzyMatching: {
      type: Boolean,
      default: true,
    },
    shouldSort: {
      type: Boolean,
      default: true,
    },
    isError: {
      type: Boolean,
      default: false,
    },
    showClearAll: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'search-change',
    'update:modelValue',
    'tag',
    'keydown',
    'paste',
    'open',
    'close',
    'select',
    'remove',
    'change',
    'clear-all',
  ],
  data() {
    return {
      closeTriggerTimeoutId: null,
      search: null,
    };
  },
  computed: {
    string() {
      return {
        clearAllTitle: this.$gettext('Clear all'),
      };
    },
    multiselectId() {
      return `${this.idPrefix}multiselect`;
    },
    sortedOptions() {
      if (this.shouldSort) {
        return this.options.toSorted(sortComparator(this.label, 'asc'));
      }
      return this.options;
    },
    filteredOptions() {
      if (this.isFuzzyMatching && this.search?.length > 0) {
        if (this.groupValues) {
          return this.sortedOptions.map((group) => {
            const groupValues = group[this.groupValues];
            return {
              ...group,
              [this.groupValues]:
                groupValues?.length > 0 ? searchFuzzyMatches({ text: this.search, collection: groupValues }) : [],
            };
          });
        }
        return searchFuzzyMatches({ text: this.search, collection: this.sortedOptions });
      }
      return this.sortedOptions;
    },
  },
  watch: {
    closeTrigger() {
      // timeout is to prevent from page scrolling on Space keydown because of input focus disappearing too early
      this.closeTriggerTimeoutId = setTimeout(this.$refs.multiselect.$refs.search.blur(), 100);
    },
    clearSearchTrigger() {
      this.setSearchValue('');
    },
  },
  mounted() {
    // adding listeners manually because we need custom behavior for keys pressed from input
    // and multiselect does not emit them
    document.getElementById(this.multiselectId)?.addEventListener('keydown', this.onKeyDown);
    document.getElementById(this.multiselectId)?.addEventListener('paste', this.onPaste);
  },
  beforeUnmount() {
    document.getElementById(this.multiselectId)?.removeEventListener('keydown', this.onKeyDown);
    document.getElementById(this.multiselectId)?.removeEventListener('paste', this.onPaste);
  },
  methods: {
    async onSearchChange(value) {
      this.search = value;
      this.$emit('search-change', value);
    },
    async onInput(value) {
      if (value === null) {
        return;
      }
      this.$emit('update:modelValue', value);
      await nextTick();
      let option = value;
      if (this.groupValues.length > 0) {
        option = [value];
      } else {
        option = value.value;
      }
      this.$emit('change', option);
    },
    onKeyDown(event) {
      // to prevent form submitting if multiselect is in form
      event.stopPropagation();

      // to make highlighted option from autocomplete add on Enter or Space
      const { pointer, search, filteredOptions } = this.$refs.multiselect;
      const isAutocompleteOptions = filteredOptions.length > 0;
      const highlightedOption = filteredOptions?.[pointer];
      const isFirstOptionNewAndHighlighted =
        filteredOptions?.[0]?.label === search && highlightedOption?.label === search;
      if (['Space', 'Enter'].includes(event.code) && isAutocompleteOptions && !isFirstOptionNewAndHighlighted) {
        this.$emit('tag', highlightedOption[this.label]);
        return;
      }

      this.$emit('keydown', event);
    },
    async onPaste(event) {
      event.preventDefault();
      this.$emit('paste', event);
    },
    onOpen() {
      clearTimeout(this.closeTriggerTimeoutId);
      this.$emit('open');
    },
    onClose() {
      this.$emit('close');
      if (!this.modal || !this.modal?.isVisible()) {
        return;
      }
      document.activeElement.blur();
    },
    setSearchValue(val) {
      this.$refs.multiselect.search = val;
    },
    updateClassesOnTags({ valuesToMatch, classesArr, updatingType }) {
      const tags = this.$refs.multiselect?.$el?.querySelectorAll('.multiselect__tag');
      tags?.forEach((tag) => {
        if (valuesToMatch.includes(tag.innerText)) {
          if (updatingType === 'add') {
            tag.classList.add(...classesArr);
          }
          if (updatingType === 'remove') {
            tag.classList.remove(...classesArr);
          }
        }
      });
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style lang="scss" scoped>
.qx-multiselect {
  width: 100%;
  min-height: 35px;
  .qx-icon {
    cursor: pointer;
    position: absolute;
    transform: rotate(0) translateY(-50%);
    right: 4px;
    top: 50%;
    font-size: 24px;
    line-height: 24px;
  }
  &.multiselect--active {
    .qx-icon {
      transform: rotate(-180deg) translateY(50%);
    }
  }
  .icon-cancel-circle {
    transform: translate(-150%, -50%) !important;
    font-size: 18px;
  }
  &.multiselect--disabled {
    opacity: 1;
    background: none;
    :deep {
      .multiselect__tags {
        color: $gray-600;
        background-color: $gray-200;
      }
    }
  }

  &.narrowed-tags-wrap {
    :deep {
      .multiselect__tags-wrap {
        padding-right: 30px;
      }
    }
  }
  :deep {
    &.error-select {
      .multiselect__tags {
        border-color: $red !important;
        &:focus-within {
          box-shadow: 0 0 0 0.1rem rgba(226, 20, 18, 0.25);
        }
      }
    }
    &.deletion-mark {
      .multiselect__tags-wrap .multiselect__tag:last-child {
        border-color: $red !important;
      }
    }
    &.input-visible {
      .multiselect__input {
        border-radius: 0;
        width: 100% !important;
        position: static !important;
      }
      .multiselect__placeholder {
        display: none;
      }
    }
    &.dropdown-hidden {
      .multiselect__content {
        display: none !important;
      }
    }
    &.multiselect--active .multiselect__tags {
      @extend %focusEffect;
    }
    .multiselect__tags {
      display: grid;
      grid-template-rows: 1fr;
      align-items: center;
      gap: 10px;
      width: 100%;
      min-height: 35px;
      padding: 2px 25px 2px 12px;
      font-weight: 400;
      color: $base-grey;
      background-color: $white;
      border-radius: 0.25rem;
      border: 1px solid $gray-400;
      font-size: 15px;
      cursor: pointer;
      transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;

      &:focus-within {
        @extend %focusEffect;
      }
    }
    .multiselect__tags-wrap {
      align-self: start;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 3px;
      overflow: overlay;
    }
    .multiselect__tag {
      background-color: $gray-200;
      color: $base-grey;
      border-radius: 2px;
      border: 1px solid $gray-200;
      padding: 5px 24px 5px 7px;
      word-break: break-all;
      font-size: 15px;
      margin: 0;
    }
    .multiselect__tag-icon {
      padding: 2px 2px 2px 0;
      &:hover {
        background-color: transparent;
      }
      &::after {
        font-size: 1.4em;
        font-weight: 400;
        line-height: 20px;
        color: $base-grey;
      }
    }
    .multiselect__single {
      width: 100%;
      max-width: 100%;
      overflow: hidden;
      display: flex;
      word-break: break-all;
    }
    .multiselect__placeholder {
      white-space: nowrap;
      max-width: 100%;
      overflow: hidden;
    }
    .multiselect__placeholder,
    .multiselect__single,
    .multiselect__input {
      margin-bottom: 0;
      padding-left: 0;
      padding-top: 0;
      background: none;
      font-size: 15px;
    }
    .multiselect__input {
      line-height: inherit;
    }
    .multiselect__option {
      padding: 3px 12px;
      min-height: 35px;
      font-size: 15px;
      line-height: inherit;
      display: flex;
      align-items: center;
      span {
        width: 100%;
        max-width: 100%;
        overflow-wrap: break-word;
        white-space: pre-wrap;
      }
      &--highlight {
        background: $b-05;
        color: inherit;
      }
      &.multiselect__option--group {
        padding: 0px;
        color: $gray-700 !important;
        background: transparent !important;
      }
      &.multiselect__option--disabled {
        padding: 3px 12px;
        pointer-events: all;
      }
    }
    .multiselect__content-wrapper {
      border: 1px solid $gray-400;
    }
    .multiselect__content {
      max-width: 100%;
    }
    .multiselect__element {
      span {
        min-height: 0px;
        &::after {
          display: none;
        }
      }
    }
  }
}
</style>
